/* Product photos rotate independently from variant selection and purchase state. */
(() => {
  'use strict';
  if (window.pouchGalleryController) { window.pouchGalleryController.init(); return; }
  const galleries = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class Gallery {
    constructor(element) {
      this.element = element;
      this.root = element.closest('[data-product-root]') || element;
      this.enabled = element.dataset.galleryAutoplay === 'true';
      this.interval = Math.max(3, Math.min(30, Number(element.dataset.galleryInterval) || 5)) * 1000;
      this.paused = false;
      this.hovered = false;
      this.focused = element.contains(document.activeElement);
      this.visible = this.inViewport();
      this.autoClick = false;
      this.timer = null;
      this.animation = null;
      this.listeners = [];
      this.toggle = element.querySelector('[data-gallery-toggle]');
      if (!this.toggle && this.enabled && this.images().length > 1) {
        this.toggle = document.createElement('button');
        this.toggle.type = 'button';
        this.toggle.className = 'pp-gallery__autoplay';
        this.toggle.dataset.galleryToggle = '';
        const label = document.createElement('span');
        label.dataset.galleryToggleLabel = '';
        this.toggle.append(label);
        element.append(this.toggle);
      }
      if (this.toggle) this.toggle.classList.add('pp-gallery__autoplay');
      // Showcase photos are presentation only. A variant deep link takes priority.
      if (element.querySelector('[data-gallery-model]') && !new URLSearchParams(window.location.search).has('variant')) {
        this.autoClick = true;
        element.querySelector('[data-gallery-model]').click();
        this.autoClick = false;
      } else if (new URLSearchParams(window.location.search).has('variant')) this.paused = true;
      this.listen(element, 'pointerenter', (event) => {
        if (event.pointerType !== 'touch') { this.hovered = true; this.refresh(); }
      });
      this.listen(element, 'pointerleave', () => { this.hovered = false; this.refresh(); });
      this.listen(element, 'focusin', () => { this.focused = true; this.refresh(); });
      this.listen(element, 'focusout', (event) => {
        this.focused = element.contains(event.relatedTarget);
        this.refresh();
      });
      this.listen(element, 'touchstart', (event) => {
        if (!event.target.closest('[data-gallery-toggle]')) this.stop();
      }, { passive: true });
      // Stop before controls modify the selected variant; never reset that choice.
      this.listen(this.root, 'pointerdown', (event) => {
        if (event.target.closest('[data-option-select], [data-bundle-choice], [data-bundle-option], [data-single-options], [data-bundle-card]')) this.stop();
      });
      this.listen(this.root, 'change', (event) => {
        if (event.target.matches('[data-option-select], [data-bundle-choice], [data-bundle-option], [name="id"]')) this.stop();
      });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.1);
          this.refresh();
        }, { threshold: [0, 0.1] });
        this.observer.observe(element);
      }
      this.refresh();
    }

    listen(node, event, callback, options) {
      node.addEventListener(event, callback, options);
      this.listeners.push(() => node.removeEventListener(event, callback, options));
    }
    images() {
      const models = [...this.element.querySelectorAll('[data-gallery-model]')];
      return models.length ? models : [...this.element.querySelectorAll('[data-media-thumb][data-media-kind="image"]')];
    }
    inViewport() {
      const rect = this.element.getBoundingClientRect();
      return rect.bottom > 40 && rect.top < window.innerHeight - 40 && rect.right > 0 && rect.left < window.innerWidth;
    }
    canPlay() {
      return this.enabled && !this.paused && !this.hovered && !this.focused && !document.hidden && !reducedMotion.matches && this.visible && this.element.isConnected && this.images().length > 1;
    }
    refresh() {
      window.clearTimeout(this.timer);
      this.timer = null;
      if (this.toggle) {
        this.toggle.hidden = !this.enabled || this.images().length <= 1;
        this.toggle.disabled = reducedMotion.matches;
        this.toggle.setAttribute('aria-pressed', String(!this.paused && !reducedMotion.matches));
        const label = reducedMotion.matches ? 'Pokaz wyłączony — ograniczenie ruchu' : this.paused ? 'Włącz pokaz zdjęć' : 'Wstrzymaj pokaz zdjęć';
        const labelNode = this.toggle.querySelector('[data-gallery-toggle-label]');
        if (labelNode) labelNode.textContent = label;
        else this.toggle.textContent = label;
        this.toggle.setAttribute('aria-label', label);
      }
      this.element.dataset.galleryPlaying = String(this.canPlay());
      if (this.canPlay()) this.timer = window.setTimeout(() => this.advance(), this.interval);
    }
    stop() {
      this.paused = true;
      this.animation?.cancel();
      this.refresh();
    }
    advance() {
      this.timer = null;
      if (!this.canPlay()) { this.refresh(); return; }
      const images = this.images();
      const current = images.findIndex((thumb) => thumb.classList.contains('is-active'));
      const next = images[(current + 1) % images.length];
      if (!next) return;
      this.autoClick = true;
      try { next.click(); } finally { this.autoClick = false; }
      // The existing theme click handler swaps only media; .click() does not focus.
      const shown = this.element.querySelector('[data-product-media]:not([hidden]) img') || this.element.querySelector('[data-variant-image]');
      if (shown?.animate && !reducedMotion.matches) {
        this.animation?.cancel();
        this.animation = shown.animate([{ opacity: 0.45 }, { opacity: 1 }], { duration: 420, easing: 'ease-out' });
      }
      this.refresh();
    }
    destroy() {
      window.clearTimeout(this.timer);
      this.animation?.cancel();
      this.observer?.disconnect();
      this.listeners.forEach((remove) => remove());
      this.listeners = [];
      galleries.delete(this.element);
    }
  }

  const init = () => {
    for (const [element, gallery] of galleries) if (!element.isConnected) gallery.destroy();
    document.querySelectorAll('.pp-gallery').forEach((element) => {
      if (!galleries.has(element)) galleries.set(element, new Gallery(element));
    });
  };
  document.addEventListener('click', (event) => {
    const element = event.target.closest('.pp-gallery');
    const gallery = element && galleries.get(element);
    if (!gallery) return;
    const toggle = event.target.closest('[data-gallery-toggle]');
    if (toggle) {
      if (!reducedMotion.matches) {
        gallery.paused = !gallery.paused;
        // Explicit Play is intentional even while the Play control still has focus.
        if (!gallery.paused) { gallery.focused = false; gallery.hovered = false; }
        gallery.refresh();
      }
      return;
    }
    const arrow = event.target.closest('[data-gallery-prev], [data-gallery-next]');
    if (arrow) {
      gallery.stop();
      const thumbs = [...element.querySelectorAll('[data-media-thumb]:not([hidden])')];
      if (!thumbs.length) return;
      const current = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
      thumbs[(current + (arrow.hasAttribute('data-gallery-next') ? 1 : -1) + thumbs.length) % thumbs.length].click();
      return;
    }
    if (event.target.closest('[data-media-thumb]') && !gallery.autoClick) gallery.stop();
  });
  document.addEventListener('visibilitychange', () => galleries.forEach((gallery) => gallery.refresh()));
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [element, gallery] of galleries) if (event.target === element || event.target.contains(element)) gallery.destroy();
  });
  reducedMotion.addEventListener('change', () => galleries.forEach((gallery) => gallery.refresh()));
  const refreshPosition = () => {
    galleries.forEach((gallery) => {
      if (!gallery.observer) {
        const visible = gallery.inViewport();
        if (visible !== gallery.visible) { gallery.visible = visible; gallery.refresh(); }
      }
    });
  };
  window.addEventListener('scroll', refreshPosition, { passive: true });
  window.addEventListener('resize', refreshPosition, { passive: true });
  window.pouchGalleryController = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
